import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import api from '../api/axios';
import EnterpriseModal from './EnterpriseModal';

/* ------------------------------------------------------------------
   /pricing  –  Employer pricing

   RULES FROM THE BRIEF
   - Prices, credits, validity, limits and features come ONLY from the admin
     API (/plans, /candidate-credit-packs, /job-boosts). Nothing
     price-related is hardcoded here.
   - "Package validity" (how long the whole package lasts) and "Individual job
     validity" (how long each posted job stays live) are shown separately.
   - How purchased credits can be used (simultaneously, one after another, or
     under an active-job cap) is NOT stated on this page. Confirm the rule with
     the business before launch, then add it as admin-managed text.
   - Features that are not live yet must be worded by admin as "once
     operational" in the plan's `features` list.
------------------------------------------------------------------- */

const FAQ_CATS = ['All', 'Job seekers', 'Employers', 'Payments'];

const FAQS = [
  { cat: 'Job seekers', q: 'Is candidate registration free?', a: 'Yes. Job seekers can create their profiles and explore opportunities without a candidate registration fee.' },
  { cat: 'Employers', q: 'Can I post my first job for free?', a: 'Eligible verified employers can access the Launch package, subject to its terms.' },
  { cat: 'Employers', q: 'Can I post multiple jobs?', a: 'Yes. Scale and Business Plus offer multiple job credits, subject to the package rules.' },
  { cat: 'Payments', q: 'Can I renew my package?', a: 'Employers can purchase another package through the available payment process.' },
  { cat: 'Payments', q: 'How are payments activated?', a: 'Successful eligible Razorpay payments automatically activate the purchased package and job credits.' },
  { cat: 'Payments', q: 'Are taxes included?', a: 'Applicable taxes are shown clearly during checkout and on invoices.' },
];
const STEPS = [
  { title: 'Choose a package', text: 'Pick the plan that fits how many people you are hiring.' },
  { title: 'Pay online', text: 'Successful eligible payments activate your package automatically.' },
  { title: 'Start hiring', text: 'Post jobs, receive applications and manage candidates.' },
];

const TRUST = [
  'Free for job seekers',
  'Taxes shown at checkout',
  'Activates after successful payment',
];

const FLAG_ROWS = [
  ['ai_features_enabled', 'AI features'],
  ['candidate_search_enabled', 'Candidate search'],
  ['advanced_filters_enabled', 'Advanced filters'],
  ['ai_matching_enabled', 'AI matching'],
  ['ai_shortlist_enabled', 'AI shortlist'],
  ['interview_tools_enabled', 'Interview tools'],
  ['analytics_enabled', 'Analytics'],
  ['bulk_actions_enabled', 'Bulk actions'],
  ['priority_support', 'Priority support'],
];

/* ------------------------------ helpers ------------------------------ */
const fmtPrice = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const days = (n) => (n == null ? '—' : `${n} days`);

const isFree = (p) => Number(p.price_monthly) === 0 || p.billing_type === 'free';
const isCustom = (p) =>
  !!p.is_custom_pricing || p.price_monthly == null || /corporate|enterprise/i.test(p.name || '');
const packageDays = (p) => p.package_validity_days ?? p.validity_days ?? null;
const planKey = (p) => p.id || p.name;

const ctaText = (p) => {
  if (p.cta_label) return p.cta_label;
  if (isCustom(p)) return 'Talk to Sales';
  if (isFree(p)) return 'Start Free';
  return `Choose ${p.name}`;
};

const countOrNone = (v) => (v == null || Number(v) === 0 ? 'None' : v);

// Bullet list: admin features first; otherwise the enabled feature flags.
const featureList = (p) => {
  const admin = Array.isArray(p.features) ? p.features.filter(Boolean) : [];
  if (admin.length) return admin;
  return FLAG_ROWS.filter(([k]) => p[k]).map(([, label]) => label);
};

// Short key facts shown as chips on the card.
const keyFacts = (p) =>
  [
    { icon: 'briefcase', text: p.job_post_limit == null ? 'Unlimited active jobs' : `${p.job_post_limit} active job${Number(p.job_post_limit) === 1 ? '' : 's'}` },
    p.job_credits != null && { icon: 'briefcase', text: `${p.job_credits} job credit${Number(p.job_credits) === 1 ? '' : 's'}` },
    p.contact_unlocks_per_job != null && { icon: 'user', text: `${p.contact_unlocks_per_job} candidate unlocks / month` },
    Number(p.featured_jobs) > 0 && { icon: 'star', text: `${p.featured_jobs} featured job${Number(p.featured_jobs) === 1 ? '' : 's'} / month` },
    Number(p.boost_credits) > 0 && { icon: 'bolt', text: `${p.boost_credits} job boost${Number(p.boost_credits) === 1 ? '' : 's'} / month` },
  ].filter(Boolean);

const LIMIT_ROWS = [
  ['Active jobs', (p) => (p.job_post_limit == null ? 'Unlimited' : p.job_post_limit)],
  ['Job credits', (p) => (p.job_credits == null ? '—' : p.job_credits)],
  ['Package validity', (p) => days(packageDays(p))],
  ['Individual job validity', (p) => days(p.job_validity_days)],
  ['Applications per job', (p) => (p.application_limit == null ? 'Unlimited' : p.application_limit)],
  ['Candidate unlocks / month', (p) => (p.contact_unlocks_per_job == null ? 'Unlimited' : countOrNone(p.contact_unlocks_per_job))],
  ['Featured jobs / month', (p) => countOrNone(p.featured_jobs)],
  ['Job boosts / month', (p) => countOrNone(p.boost_credits)],
  ['Recruiter seats', (p) => p.recruiter_seats ?? 1],
];

const COMPARE_ROWS = [
  ['Job credits', (p) => (p.job_credits == null ? '—' : p.job_credits)],
  ['Package validity', (p) => days(packageDays(p))],
  ['Individual job validity', (p) => days(p.job_validity_days)],
  ['Active job limit', (p) => (p.job_post_limit == null ? '—' : p.job_post_limit)],
];

/* ------------------------------ icons ------------------------------ */
const ICONS = {
  check: <path d="M20 6L9 17l-5-5" />,
  briefcase: <><rect x="2" y="7" width="20" height="13" rx="2" /><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2M2 12h20" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" /></>,
  star: <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3z" />,
  bolt: <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />,
  shield: <><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" /><path d="M9 12l2 2 4-4" /></>,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
};

function Icon({ name, className = 'w-full h-full' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

/* ------------------------------ plan card ------------------------------ */
function PlanCard({ plan, ctaTo, onDetails }) {
  const free = isFree(plan);
  const best = !!plan.is_best_value;
  const facts = keyFacts(plan);
  const allFeatures = featureList(plan);
  const features = allFeatures.slice(0, 4);
  const more = allFeatures.length - features.length;
  const pkg = packageDays(plan);

  return (
    <div
      className={`relative flex flex-col rounded-3xl p-5 transition-all duration-300 hover:-translate-y-1.5 ${
        best
          ? 'order-first sm:order-none border-2 border-[#DB2777] bg-gradient-to-b from-[#FDF4FF] via-white to-white shadow-[0_30px_60px_-25px_rgba(124,58,237,0.55)]'
          : 'border border-purple-100 bg-white hover:border-[#7C3AED]/40 hover:shadow-[0_24px_50px_-28px_rgba(124,58,237,0.45)]'
      }`}
    >
      {(best || plan.is_promotional) && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold brand-gradient text-white px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
          {plan.promo_label || (best ? 'Best value' : 'Offer')}
        </span>
      )}

      <p className="text-sm font-extrabold uppercase tracking-widest text-[#7C3AED]">{plan.name}</p>
      {plan.short_description && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{plan.short_description}</p>
      )}

      <div className="mt-3 mb-4">
        <p className="text-4xl leading-none font-extrabold tracking-tight text-[#1E0F3D] tabular-nums">
          {free ? 'Free' : fmtPrice(plan.price_monthly)}
        </p>
        <p className="text-xs text-gray-400 mt-1.5">
          {free ? 'No payment needed' : 'per month'}
          {!free && plan.gst_percent != null && ` + ${plan.gst_percent}% GST`}
        </p>
      </div>

      <Link
        to={ctaTo}
        className={`shine-btn text-center py-3 rounded-xl text-sm font-bold transition ${
          best
            ? 'brand-gradient text-white shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)]'
            : 'border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F0FA]'
        }`}
      >
        {ctaText(plan)}
      </Link>

      {facts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {facts.map((f) => (
            <span key={f.text} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#5B21B6] bg-[#F5F0FA] rounded-full pl-1.5 pr-2.5 py-1">
              <span className="w-3 h-3"><Icon name={f.icon} /></span>{f.text}
            </span>
          ))}
        </div>
      )}

      <dl className={`grid gap-2 mt-3 text-xs ${pkg != null ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div className="rounded-lg bg-[#F5F0FA] px-3 py-1.5">
          <dt className="text-gray-500 leading-tight">Job validity</dt>
          <dd className="font-extrabold text-[#1E0F3D]">{days(plan.job_validity_days)}</dd>
        </div>
        {pkg != null && (
          <div className="rounded-lg bg-[#F5F0FA] px-3 py-1.5">
            <dt className="text-gray-500 leading-tight">Package validity</dt>
            <dd className="font-extrabold text-[#1E0F3D]">{days(pkg)}</dd>
          </div>
        )}
      </dl>

      {features.length > 0 && (
        <ul className="space-y-1.5 mt-4 flex-1">
          {features.map((b) => (
            <li key={b} className="flex items-start gap-2 text-[13px] text-gray-600">
              <span className="w-4 h-4 mt-0.5 rounded-full brand-gradient text-white p-0.5 shrink-0"><Icon name="check" /></span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={onDetails}
        className="inline-flex items-center justify-center gap-1.5 mt-4 text-sm font-semibold text-[#DB2777] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 rounded"
      >
        {more > 0 ? `+${more} more · ` : ''}View full details <span className="w-3.5 h-3.5"><Icon name="arrow" /></span>
      </button>
    </div>
  );
}

/* --------------------------- add-on pack cards --------------------------- */
function PackCard({ label, big, bigLabel, price, chip, text, to, cta, delay }) {
  return (
    <div
      style={{ animationDelay: `${delay}ms` }}
      className="swap flex flex-col rounded-3xl overflow-hidden bg-white border border-purple-100 shadow-[0_20px_50px_-30px_rgba(124,58,237,0.4)] transition hover:-translate-y-1.5"
    >
      <div className="brand-gradient text-white px-6 pt-5 pb-6">
        <p className="text-[11px] font-bold uppercase tracking-widest text-white/80">{label}</p>
        <p className="text-5xl font-extrabold leading-none mt-2">{big}</p>
        <p className="text-sm text-white/90 mt-1">{bigLabel}</p>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-end justify-between gap-3 mb-3">
          <p className="text-3xl font-extrabold tabular-nums">{fmtPrice(price)}</p>
          <p className="text-xs font-semibold text-[#7C3AED] bg-[#F5F0FA] px-2.5 py-1 rounded-full whitespace-nowrap">{chip}</p>
        </div>
        <p className="text-sm text-gray-600 flex-1 mb-5">{text}</p>
        <Link to={to} className="shine-btn text-center px-6 py-3 rounded-xl font-semibold text-sm brand-gradient text-white hover:opacity-90 transition">{cta}</Link>
      </div>
    </div>
  );
}

/* ------------------------------ page ------------------------------ */
function Pricing() {
  const [plans, setPlans] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [jobBoosts, setJobBoosts] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [openFaq, setOpenFaq] = useState(null);
    const [faqCat, setFaqCat] = useState('All');
  const [selectedPlanKey, setSelectedPlanKey] = useState(null);
  const [planTab, setPlanTab] = useState('plans'); // 'plans' | 'credits' | 'boost'


    const [showEnterprise, setShowEnterprise] = useState(false);
  const [corp, setCorp] = useState(null);
  useEffect(() => {
    api.get('/enterprise-card').then(({ data }) => setCorp(data || null)).catch(() => {});
  }, []);
  const corpBadge = corp?.badge || 'Corporate';
  const corpTitle = corp?.title || 'Custom pricing';
  const corpDesc = corp ? (corp.description || '') : 'For companies with large-scale recruitment needs.';
  const corpButton = corp?.button_label || 'Talk to Sales';
  const corpFeatures = Array.isArray(corp?.features) && corp.features.length
    ? corp.features
    : ['Bulk job posting', 'Multiple HR users', 'Multi-location recruitment', 'Centralised recruitment dashboard', 'Custom analytics', 'API integrations, subject to availability', 'Dedicated account management'];

  const isLoggedIn = !!localStorage.getItem('token');
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const load = () => {
    setStatus('loading');
    const toList = (res) => (Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    Promise.allSettled([
      api.get('/plans', { params: { type: 'subscription' } }),
      api.get('/candidate-credit-packs'),
      api.get('/job-boosts'),
    ]).then(([p, c, b]) => {
      setCreditPacks(c.status === 'fulfilled' ? toList(c.value) : []);
      setJobBoosts(b.status === 'fulfilled' ? toList(b.value) : []);
      if (p.status === 'fulfilled') {
        setPlans(toList(p.value));
        setStatus('ready');
      } else {
        setStatus('error');
      }
    });
  };
  useEffect(load, []);

  const sorted = useMemo(
    () => [...plans].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [plans]
  );
  const regular = sorted.filter((p) => !isCustom(p));
  const custom = sorted.filter((p) => isCustom(p));
  const selectedPlan = sorted.find((p) => planKey(p) === selectedPlanKey) || null;

  const gridCols =
    regular.length >= 4 ? 'lg:grid-cols-4' : regular.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2';

  const ctaFor = (p) => {
    if (isCustom(p)) return '/contact';
    if (!isLoggedIn) return '/register';
    return user?.role === 'employer' ? '/employer/settings/billing?tab=plans' : '/register';
  };
  const billingLink = (tab) =>
    !isLoggedIn ? '/register' : user?.role === 'employer' ? `/employer/settings/billing?tab=${tab}` : '/register';

  // Modal: Esc to close, lock page scroll
  useEffect(() => {
    if (!selectedPlan) return;
    const onKey = (e) => e.key === 'Escape' && setSelectedPlanKey(null);
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [selectedPlan]);

  // SEO
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Affordable Job Posting Packages in India | PlanB Jobs';
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute('content');
    meta.setAttribute(
      'content',
      'Explore affordable job posting plans on PlanB Jobs. Choose Launch, Solo, Scale, Business Plus or Corporate recruitment packages.'
    );
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute('content', prevDesc);
    };
  }, []);

  const visibleFlagRows = FLAG_ROWS.filter(([key]) => sorted.some((p) => p[key] != null));
  const ready = status === 'ready';
  const planTabs = [
    { key: 'plans', label: '💳 Hiring Plans', count: sorted.length },
    { key: 'credits', label: '👤 Candidate Credits', count: creditPacks.length },
    { key: 'boost', label: '⭐ Job Boost', count: jobBoosts.length },
  ].filter((t) => t.count > 0);
  const activeTab = planTabs.some((t) => t.key === planTab) ? planTab : planTabs[0]?.key;

  const navPills = [
    { href: '#packages', label: 'Hiring plans', show: true },

    { href: '#compare', label: 'Compare', show: ready && sorted.length > 1 },
    { href: '#faq', label: 'FAQ', show: true },
  ].filter((p) => p.show);

  return (
    <div className="pricing-root min-h-screen bg-white overflow-x-clip text-[#1E0F3D]">
      <style>{`
        .brand-gradient { background-image: linear-gradient(90deg,#7C3AED,#DB2777); }
        .pricing-root h2 {
          display: inline-block;
          background-image: linear-gradient(90deg,#7C3AED,#DB2777);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }
        .pricing-root .brand-gradient h2, .pricing-root .dark-h2 { background-image: none; -webkit-text-fill-color: #fff; color: #fff; }
        @keyframes rise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
        @keyframes swap { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes pulse-soft { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
        .rise { animation: rise .8s cubic-bezier(.2,.8,.2,1) both; }
        .swap { animation: swap .35s ease-out both; }
        .skeleton { animation: pulse-soft 1.4s ease-in-out infinite; }
        .shine-btn { position: relative; overflow: hidden; }
        .shine-btn::after { content: ''; position: absolute; top: 0; left: 0; width: 30%; height: 100%; background: rgba(255,255,255,.3); transform: translateX(-120%) skewX(-20deg); transition: transform .7s ease; pointer-events: none; }
        .shine-btn:hover::after { transform: translateX(420%) skewX(-20deg); }
        html { scroll-behavior: smooth; }
        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; }
          .rise, .swap, .skeleton { animation: none; }
          .shine-btn::after { transition: none; }
        }
      `}</style>

      <Navbar user={user} />

      {/* ================================ HERO ================================ */}
      <section className="relative bg-gradient-to-br from-[#FBF5FF] via-white to-[#FDF0F6] pt-14 pb-14 lg:pt-20 lg:pb-16">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#7C3AED]/15 blur-3xl" />
          <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#DB2777]/12 blur-3xl" />
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(124,58,237,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center rise">
          <h1 className="mb-5 text-[40px] sm:text-[56px] font-extrabold leading-[1.05] tracking-tight">
            <span className="block mx-auto w-fit bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent">Better Hiring.</span>
            <span className="block mx-auto w-fit bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent">Smarter Spending.</span>
          </h1>
          <p className="max-w-xl mx-auto text-lg leading-7 text-gray-600">
            Whether you need one employee or an entire team, PlanB Jobs has a recruitment package designed around your hiring needs.
          </p>

          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-7">
            {TRUST.map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm font-medium text-gray-600">
                <span className="w-5 h-5 rounded-full brand-gradient text-white p-1"><Icon name="check" /></span>{t}
              </li>
            ))}
          </ul>

          <nav aria-label="On this page" className="flex flex-wrap justify-center gap-2 mt-8">
            {navPills.map((p) => (
              <a key={p.href} href={p.href} className="px-4 py-2 rounded-full text-sm font-semibold text-[#7C3AED] bg-white border border-purple-100 hover:border-[#7C3AED] hover:bg-[#F5F0FA] transition">
                {p.label}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* ============================== PACKAGES ============================== */}
      <section id="packages" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Choose your hiring plan</h2>
          <p className="text-gray-500 max-w-xl mx-auto">Start free, upgrade when you need more jobs and more reach. Select a card to see everything included.</p>
        </div>

        {ready && planTabs.length > 1 && (
          <div className="flex justify-center mb-10">
            <div className="inline-flex flex-wrap justify-center bg-white border border-purple-100 rounded-2xl p-1.5 shadow-sm" role="tablist">
              {planTabs.map((t) => (
                <button
                  key={t.key} type="button" role="tab" aria-selected={activeTab === t.key} onClick={() => setPlanTab(t.key)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 ${activeTab === t.key ? 'brand-gradient text-white shadow-md shadow-[#7C3AED]/25' : 'text-gray-500 hover:text-[#1E0F3D]'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {status === 'loading' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" aria-busy="true" aria-label="Loading packages">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-[520px] rounded-3xl bg-[#F5F0FA]" />
            ))}
          </div>
        )}

        {status === 'error' && (
          <div className="text-center max-w-md mx-auto py-10">
            <p className="font-bold text-lg mb-2">We could not load the packages.</p>
            <p className="text-sm text-gray-500 mb-5">Check your connection and try again.</p>
            <button type="button" onClick={load} className="brand-gradient text-white px-8 py-3 rounded-xl font-semibold text-sm hover:opacity-90 transition">
              Try again
            </button>
          </div>
        )}

        {ready && sorted.length === 0 && (
          <p className="text-center text-gray-500 py-10">Packages will be available here soon.</p>
        )}

        {ready && activeTab === 'plans' && regular.length > 0 && (
          <div className={`swap grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-5 pt-3 items-stretch`}>
            {regular.map((p) => (
              <PlanCard key={planKey(p)} plan={p} ctaTo={ctaFor(p)} onDetails={() => setSelectedPlanKey(planKey(p))} />
            ))}
          </div>
        )}

        {ready && activeTab === 'credits' && (
          <div key="credits" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {creditPacks.map((pack, i) => {
              const unlocks = Number(pack.unlocks ?? pack.credits) || 1;
              return (
                <PackCard
                  key={pack.id ?? i}
                  delay={i * 80}
                  label="Credit pack"
                  big={pack.credits ?? pack.unlocks}
                  bigLabel="Credits"
                  price={pack.price}
                  chip={`₹${(Number(pack.price) / unlocks).toFixed(1)} / unlock`}
                  text={`${unlocks} candidate contact unlocks. Never expires.`}
                  to={billingLink('credits')}
                  cta="Buy top-up"
                />
              );
            })}
          </div>
        )}

        {ready && activeTab === 'boost' && (
          <div key="boost" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobBoosts.map((tier, i) => (
              <PackCard
                key={tier.id ?? i}
                delay={i * 80}
                label="Boost pack"
                big={tier.duration_days}
                bigLabel="Days"
                price={tier.price}
                chip={`₹${(Number(tier.price) / Number(tier.duration_days || 1)).toFixed(1)} / day`}
                text="Higher search visibility and a Boost badge on your job."
                to={billingLink('boost')}
                cta="Buy Boost"
              />
            ))}
          </div>
        )}

                {ready && activeTab === 'plans' && custom.length === 0 && !(corp && corp.is_active === false) && (
          <div className="mt-8 relative overflow-hidden rounded-3xl bg-[#17142A] p-7 lg:p-9 text-white">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#7047FF]/30 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 items-center">
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-[#C7F464] text-[#17142A] px-3 py-1 rounded-full">
                  {corpBadge}
                </span>
                <p className="text-3xl sm:text-4xl font-bold mt-4" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                  {corpTitle}
                </p>
                <p className="text-sm text-white/70 mt-2 max-w-sm">{corpDesc}</p>
                {isLoggedIn && user?.role === 'employer' ? (
                  <button
                    type="button"
                    onClick={() => setShowEnterprise(true)}
                    className="mt-6 inline-block py-3 px-8 rounded-xl text-sm font-bold bg-[#C7F464] text-[#17142A] hover:bg-[#B6E84A] hover:shadow-lg transition"
                  >
                    {corpButton}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="mt-6 inline-block py-3 px-8 rounded-xl text-sm font-bold bg-[#C7F464] text-[#17142A] hover:bg-[#B6E84A] hover:shadow-lg transition"
                  >
                    {corpButton}
                  </Link>
                )}
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 items-start">
                {corpFeatures.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/90">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#C7F464] text-[#17142A] flex items-center justify-center text-[11px] font-bold shrink-0">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Corporate / custom-priced plans */}
        {ready && activeTab === 'plans' && custom.map((p) => (
          <div key={planKey(p)} className="mt-8 rounded-3xl bg-[#17142A] text-white p-7 lg:p-9 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C7F464] mb-2">{p.name}</p>
              <h3 className="text-2xl font-bold mb-2">Hiring at a larger scale?</h3>
              <p className="text-white/70 max-w-xl text-sm">
                {p.short_description || 'Explore bulk recruitment and customised solutions for larger hiring needs.'}
              </p>
              {featureList(p).length > 0 && (
                <ul className="flex flex-wrap gap-2 mt-4">
                  {featureList(p).slice(0, 5).map((f) => (
                    <li key={f} className="text-xs font-semibold bg-white/10 border border-white/15 rounded-full px-3 py-1.5">{f}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to={ctaFor(p)} className="bg-white text-[#7C3AED] px-8 py-3.5 rounded-xl font-bold text-sm hover:opacity-90 transition">{ctaText(p)}</Link>
              <button type="button" onClick={() => setSelectedPlanKey(planKey(p))} className="border border-white/40 text-white px-6 py-3.5 rounded-xl font-semibold text-sm hover:bg-white/10 transition">
                View details
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* ============================ HOW IT WORKS ============================ */}
      {ready && (
        <section className="bg-gradient-to-br from-[#FBF5FF] to-[#FDF0F6] py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-10">Up and running in three steps</h2>
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {STEPS.map((s, i) => (
                <li key={s.title} className="rounded-3xl bg-white border border-purple-100 p-7 transition hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                  <span className="w-11 h-11 rounded-xl brand-gradient text-white flex items-center justify-center font-bold mb-5">{i + 1}</span>
                  <h3 className="text-lg font-bold mb-1.5">{s.title}</h3>
                  <p className="text-sm text-gray-500">{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* =========================== CANDIDATE CREDITS ===========================
      {ready && creditPacks.length > 0 && (
        <section id="credits" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="max-w-2xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Candidate credits</h2>
            <p className="text-gray-500">Top up when you want to unlock more candidate contacts. The more you buy, the less each unlock costs.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {creditPacks.map((pack, i) => {
              const unlocks = Number(pack.unlocks ?? pack.credits) || 1;
              return (
                <PackCard
                  key={pack.id ?? i}
                  delay={i * 80}
                  label="Credit pack"
                  big={pack.credits ?? pack.unlocks}
                  bigLabel="Credits"
                  price={pack.price}
                  chip={`₹${(Number(pack.price) / unlocks).toFixed(1)} / unlock`}
                  text={`${unlocks} candidate contact unlocks. Never expires.`}
                  to={billingLink('credits')}
                  cta="Buy top-up"
                />
              );
            })}
          </div>
        </section>
      )} */}

      {/* ================================ JOB BOOST ================================ */}
      {/* {ready && jobBoosts.length > 0 && (
        <section id="boost" className="scroll-mt-24 bg-gradient-to-br from-[#FBF5FF] to-[#FDF0F6] py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Job boost</h2>
              <p className="text-gray-500">Give an important vacancy higher search visibility and a Boost badge for the duration you choose.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobBoosts.map((tier, i) => (
                <PackCard
                  key={tier.id ?? i}
                  delay={i * 80}
                  label="Boost pack"
                  big={tier.duration_days}
                  bigLabel="Days"
                  price={tier.price}
                  chip={`₹${(Number(tier.price) / Number(tier.duration_days || 1)).toFixed(1)} / day`}
                  text="Higher search visibility and a Boost badge on your job."
                  to={billingLink('boost')}
                  cta="Buy Boost"
                />
              ))}
            </div>
          </div>
        </section>
      )} */}

      {/* ============================== COMPARE ============================== */}
      {ready && sorted.length > 1 && (
        <section id="compare" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Compare packages</h2>
          <p className="text-gray-500 max-w-xl mb-8">
            Package validity is how long your package stays active. Individual job validity is how long each job stays live once posted.
          </p>

          <div className="overflow-x-auto rounded-3xl border border-purple-100 bg-white shadow-[0_20px_50px_-35px_rgba(124,58,237,0.4)]">
            <table className="w-full min-w-[760px] text-sm text-left border-separate border-spacing-0">
              <thead>
                <tr>
                  <th scope="col" className="sticky left-0 z-10 bg-white px-5 py-4 font-semibold text-gray-500 w-52 border-b border-purple-100">Feature</th>
                  {sorted.map((p) => (
                    <th
                      key={planKey(p)}
                      scope="col"
                      className={`px-5 py-4 font-extrabold border-b border-purple-100 ${p.is_best_value ? 'bg-[#FDF4FF] text-[#DB2777]' : 'text-[#1E0F3D]'}`}
                    >
                      {p.name}
                      {p.is_best_value && <span className="block text-[10px] font-bold uppercase tracking-wider text-[#DB2777]/80">Best value</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(([label, get]) => (
                  <tr key={label}>
                    <th scope="row" className="sticky left-0 z-10 bg-white px-5 py-3.5 font-medium text-gray-600 border-b border-[#F3E8FF]">{label}</th>
                    {sorted.map((p) => (
                      <td key={planKey(p)} className={`px-5 py-3.5 font-semibold border-b border-[#F3E8FF] ${p.is_best_value ? 'bg-[#FDF4FF]/60' : ''}`}>
                        {isCustom(p) ? 'Custom' : get(p)}
                      </td>
                    ))}
                  </tr>
                ))}
                {visibleFlagRows.map(([key, label]) => (
                  <tr key={key}>
                    <th scope="row" className="sticky left-0 z-10 bg-white px-5 py-3.5 font-medium text-gray-600 border-b border-[#F3E8FF]">{label}</th>
                    {sorted.map((p) => (
                      <td key={planKey(p)} className={`px-5 py-3.5 border-b border-[#F3E8FF] ${p.is_best_value ? 'bg-[#FDF4FF]/60' : ''}`}>
                        {p[key]
                          ? <span className="inline-block w-5 h-5 rounded-full brand-gradient text-white p-1" role="img" aria-label="Included"><Icon name="check" /></span>
                          : <span className="text-gray-300" aria-label="Not included">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ================================ FAQ ================================ */}
           <section id="faq" className="scroll-mt-24 bg-gradient-to-br from-[#FBF5FF] to-[#FDF0F6] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.7fr] gap-10 lg:gap-16 items-start">

          {/* Left: heading + contact card */}
          <div className="lg:sticky lg:top-28">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-[#7C3AED] bg-white border border-purple-100 px-3 py-1 rounded-full mb-4">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Pricing questions.</h2>
            <p className="text-gray-500 mb-8">Short, honest answers about plans, payments and renewals.</p>

            <div className="rounded-2xl bg-[#17142A] text-white p-6">
              <p className="font-bold text-lg">Still have a question?</p>
              <p className="text-sm text-white/70 mt-1 mb-5">Our team will help you pick the right plan.</p>
              <Link to="/contact" className="inline-block bg-[#C7F464] text-[#17142A] px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-[#B6E84A] transition">
                Contact us
              </Link>
            </div>
          </div>

          {/* Right: tabs + accordion */}
          <div>
            <div className="flex flex-wrap gap-2 mb-5" role="tablist">
              {FAQ_CATS.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={faqCat === c}
                  onClick={() => { setFaqCat(c); setOpenFaq(null); }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                    faqCat === c
                      ? 'brand-gradient text-white border-transparent shadow-md shadow-[#7C3AED]/25'
                      : 'bg-white text-gray-600 border-purple-100 hover:border-[#7C3AED] hover:text-[#7C3AED]'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {FAQS.filter((f) => faqCat === 'All' || f.cat === faqCat).map((f, i) => {
                const isOpen = openFaq === f.q;
                return (
                  <div
                    key={f.q}
                    className={`rounded-2xl border bg-white transition ${
                      isOpen
                        ? 'border-[#7C3AED]/40 shadow-[0_20px_40px_-25px_rgba(124,58,237,0.5)]'
                        : 'border-purple-100 hover:border-[#7C3AED]/30'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : f.q)}
                      aria-expanded={isOpen}
                      aria-controls={`pricing-faq-${i}`}
                      className="w-full flex items-center gap-4 text-left px-5 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 rounded-2xl"
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${isOpen ? 'brand-gradient text-white' : 'bg-[#F5F0FA] text-[#7C3AED]'}`}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1 font-semibold text-[#1E0F3D]">{f.q}</span>
                      <span className={`w-7 h-7 rounded-full border border-purple-100 flex items-center justify-center text-[#DB2777] text-lg leading-none shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-45 bg-[#FDF4FF]' : ''}`} aria-hidden="true">
                        +
                      </span>
                    </button>

                    <div
                      id={`pricing-faq-${i}`}
                      className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-5 pb-5 pl-[4.25rem] text-sm text-gray-500 leading-relaxed">{f.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </section>

      {/* ============================== FINAL CTA ============================== */}
      <section className="px-6 lg:px-8 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto brand-gradient rounded-[2rem] relative overflow-hidden px-8 lg:px-16 py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)]">
          <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Start hiring with PlanB Jobs.</h2>
            <p className="text-white/85 max-w-md">Pick a package that fits your hiring plans, or talk to us about larger recruitment needs.</p>
          </div>
          <div className="relative flex flex-wrap gap-3 lg:justify-self-end">
            <Link to={isLoggedIn ? '/employer/jobs' : '/register'} className="bg-white text-[#7C3AED] px-8 py-3.5 rounded-xl font-bold text-sm hover:opacity-90 transition">Post a Job</Link>
            <Link to="/contact" className="border border-white/50 text-white px-8 py-3.5 rounded-xl font-semibold text-sm hover:bg-white/10 transition">Talk to Sales</Link>
          </div>
        </div>
      </section>

      {/* ============================ PLAN DETAILS MODAL ============================ */}
      {selectedPlan && (() => {
        const plan = selectedPlan;
        const isCust = isCustom(plan);
        const list = featureList(plan);
        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedPlanKey(null)} role="dialog" aria-modal="true" aria-label={`${plan.name} details`}>
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="brand-gradient text-white p-6 rounded-t-3xl">
                <button type="button" onClick={() => setSelectedPlanKey(null)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 font-bold" aria-label="Close">✕</button>
                <p className="text-sm font-bold uppercase tracking-widest text-white/80">{plan.name}</p>
                {isCust ? (
                  <p className="text-3xl font-extrabold mt-1">Custom pricing</p>
                ) : (
                  <p className="text-4xl font-extrabold mt-1">
                    {isFree(plan) ? 'Free' : fmtPrice(plan.price_monthly)}{' '}
                    {!isFree(plan) && <span className="text-sm font-normal text-white/80">/ month{plan.gst_percent != null && ` + ${plan.gst_percent}% GST`}</span>}
                  </p>
                )}
                {plan.short_description && <p className="text-sm text-white/90 mt-2">{plan.short_description}</p>}
              </div>

              <div className="p-6 space-y-6">
                {list.length > 0 && (
                  <div>
                    <h4 className="font-bold text-[#3B0764] mb-3">What's included</h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {list.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-sm text-gray-600">
                          <span className="w-5 h-5 mt-0.5 rounded-full brand-gradient text-white p-1 shrink-0"><Icon name="check" /></span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!isCust && (
                  <div>
                    <h4 className="font-bold text-[#3B0764] mb-3">Plan limits</h4>
                    <div className="border border-[#F3E8FF] rounded-2xl divide-y divide-[#F3E8FF] overflow-hidden">
                      {LIMIT_ROWS.map(([rowLabel, get]) => (
                        <div key={rowLabel} className="flex justify-between gap-4 px-4 py-2.5 text-sm odd:bg-[#FBF5FF]/50">
                          <span className="text-gray-600">{rowLabel}</span>
                          <span className="font-semibold text-[#3B0764]">{get(plan)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-bold text-[#3B0764] mb-3">Features</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {FLAG_ROWS.map(([key, rowLabel]) => (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        {plan[key] ? <span className="text-[#9333EA] font-bold">✓</span> : <span className="text-gray-300">—</span>}
                        <span className={plan[key] ? 'text-gray-700' : 'text-gray-400'}>{rowLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link to={ctaFor(plan)} className="shine-btn block text-center w-full py-3.5 rounded-xl text-sm font-bold text-white brand-gradient shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)]">
                  {ctaText(plan)}
                </Link>
              </div>
            </div>
          </div>
        );
      })()}
      <EnterpriseModal open={showEnterprise} onClose={() => setShowEnterprise(false)} />


      <Footer />
    </div>
  );
}

export default Pricing;